import React, {ReactNode} from 'react';
import styled from 'styled-components';
import {DatePicker, FlexColumn, NumberField, tokensForThemeProvider} from '@forecast-it/design-system';
import {isValid, parseISO} from 'date-fns';
import {useIntl} from 'react-intl';

interface TypedValueInputProps {
	dataType: 'number' | 'date';
	value: string | undefined;
	/** The canonical string to store, or `null` to store nothing (cleared, or not a valid number). A date
	 * still being typed is not reported at all, so the stored value stays put until the date is whole. */
	onChange: (value: string | null) => void;
	operatorPicker: ReactNode;
	dataCy: string;
}

const tokens = tokensForThemeProvider.sky.sys;
const NUMBER = /^-?\d+(\.\d+)?$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** `DropdownList.Content` pads its content vertically only, so the bare design-system inputs need
 * their own horizontal inset or they sit flush against the popover's edges. */
const Inset = styled.div`
	padding: 0 ${tokens.spacing['400']};
`;

/** The number field reports a `number`, and `NaN` when it is empty or holds text it cannot read. Only
 * a finite number whose plain decimal form matches what report-service parses is stored: a very
 * large or small magnitude prints in exponent form, which the filter would reject. */
function numberToValue(next: number): string | null {
	if (typeof next !== 'number' || !Number.isFinite(next)) {
		return null;
	}
	const text = String(next);
	return NUMBER.test(text) ? text : null;
}

/** A date the filter can use: `yyyy-MM-dd`, a real calendar day, with a four-digit year. The picker
 * reports a date at every keystroke of the year, so typing 2026 passes through 0002, 0020 and 0202;
 * those are valid days but never what the reader means, and storing them would refetch the report
 * for each one. */
function isStorableDate(next: string): boolean {
	return ISO_DATE.test(next) && isValid(parseISO(next)) && Number(next.slice(0, 4)) >= 1000;
}

/** The value control for a number or date company custom field's condition. The other condition kinds
 * pick from a candidate list; a number or a date has no list, so the panel shows the design-system
 * input instead. It emits the canonical string report-service's task filter parses, and nothing at all
 * for an entry that is not a valid number or date, so a malformed value is never stored. */
export function TypedValueInput({dataType, value, onChange, operatorPicker, dataCy}: TypedValueInputProps): JSX.Element {
	const {formatMessage} = useIntl();
	const label = formatMessage({id: 'ai_report_builder.conditions.typed_value_label'});
	// `NaN` is how the number field represents "empty" while staying controlled.
	const numberValue = value !== undefined && NUMBER.test(value) ? Number(value) : NaN;
	return (
		<FlexColumn gap={'xs'}>
			{operatorPicker ? <Inset>{operatorPicker}</Inset> : null}
			<Inset>
				{dataType === 'number' ? (
					<NumberField
						label={label}
						value={numberValue}
						maximumFractionDigits={10}
						onChange={(next: number) => onChange(numberToValue(next))}
						data-cy={dataCy}
					/>
				) : (
					<DatePicker
						label={label}
						value={value && ISO_DATE.test(value) ? value : undefined}
						onChange={(next: string | undefined) => {
							if (!next) {
								onChange(null);
							} else if (isStorableDate(next)) {
								onChange(next);
							}
							// Anything else is a date still being typed: keep what is stored until it is whole.
						}}
						data-cy={dataCy}
					/>
				)}
			</Inset>
		</FlexColumn>
	);
}
